import React, { useState } from 'react';
import { Box, DialogTitle, Dialog, DialogContent, DialogActions, Alert, Button } from '@mui/material';
import { useFormikContext, Formik } from 'formik';
import DebounceTextField from '../../../components/DebounceTextfield';
import { AgentInputType } from '../../../types/AgentInputType.types';
import DismissedDialogValid from '../../../types/validSchemas/DismissedDialog.valid';
import { MARKERS } from '../../../features/usage-tracking/usageKeys';
import { FormUsage, useFormUsageMachine } from '../../../features/usage-tracking/hooks/useFormUsage';
import { useDialogUsage } from '../../../features/usage-tracking/hooks/useDialogUsage';

function AgentFormDismiss() {
  const { values: mainValues, setFieldValue } = useFormikContext<AgentInputType>();
  const [open, setOpen] = useState(false);
  // RITA-1504: Die Maske des Kündigungsdialogs gilt als geöffnet, solange der Dialog offen ist;
  // der Automat liegt hier, weil der Ausgang am "Setzen"-Knopf gemeldet wird.
  const formUsage = useFormUsageMachine('berater.entlassen.formular');
  // RITA-1504 (Entwurf E): Derselbe Vorgang hat zwei Seiten — die Maske meldet ihren Ausgang,
  // der Dialog seine Entscheidung. Beide Namen sind verschieden, damit die Auswertung sie nicht
  // verwechselt.
  const dialogUsage = useDialogUsage('berater.entlassen.dialog');

  // Jeder Weg aus dem Dialog außer "Setzen" ist ein Abbruch; nach der Bestätigung schluckt der
  // Riegel das Schließen, das der "Setzen"-Knopf selbst auslöst.
  const handleClose = (): void => {
    dialogUsage.cancelled();
    setOpen(false);
  };

  const handleOpen = (): void => {
    dialogUsage.reset();
    setOpen(true);
  };

  return (
    <Box sx={{ paddingTop: '5%' }}>
      <Button
        onClick={() => {
          handleOpen();
        }}
        variant="contained"
        fullWidth
        disabled={!!mainValues.dismissed_at}
        color="error"
        data-usage={MARKERS.beraterEntlassen}
      >
        Berater kündigen
      </Button>

      <Formik
        enableReinitialize
        validateOnMount
        initialValues={mainValues}
        onSubmit={(values) => {
          setFieldValue('dismissed_at', values.dismissed_at);
          setFieldValue('dismissal_reason', values.dismissal_reason);
        }}
        validationSchema={DismissedDialogValid}
      >
        {({ values, handleSubmit, handleChange, handleBlur, errors, isValid, resetForm, dirty, validateForm }) => (
          <>
            <FormUsage name="berater.entlassen.formular" machine={formUsage} active={open} />
            <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
              <DialogTitle>Kündigung einstellen</DialogTitle>
              <DialogContent>
                <Box m={1} display="flex" flexDirection="column" minWidth="400px">
                  <DebounceTextField
                    type="date"
                    label="Kündigungsdatum"
                    name="dismissed_at"
                    value={values.dismissed_at}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={errors.dismissed_at && errors.dismissed_at}
                    error={Boolean(errors.dismissed_at) || false}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>
                <Box>
                  <DebounceTextField
                    label="Kündigungs Notiz"
                    name="dismissal_reason"
                    value={values.dismissal_reason || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    multiline
                    rows={3}
                    helperText={errors.dismissal_reason && errors.dismissal_reason}
                    error={Boolean(errors.dismissal_reason) || false}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <Alert variant="filled" severity="warning">
                    <div>Achtung: Alle Leistungen werden zum Monatsende des Kündigungsmonats geschlossen</div>
                    <div className="flex pl-1">
                      <div>{`Achtung: Statusänderung auf "gekündigt" erfolgt automatisch zum letzten Tag des Kündigungsmonats`}</div>
                    </div>
                  </Alert>
                </Box>
              </DialogContent>
              <DialogActions disableSpacing={false}>
                <Button
                  autoFocus
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                  variant="outlined"
                >
                  Abbrechen
                </Button>

                <Button
                  onClick={async () => {
                    // KI-Review !727: isValid hinkt der asynchronen Prüfung nach — der Knopf kann
                    // noch bedienbar sein, obwohl Formik schon einen ungültigen Wert hält. Deshalb
                    // wird der Ausgang aus der Prüfung selbst gemeldet, bevor der Dialog zugeht
                    // (handleSubmit prüft erst im nächsten Arbeitsschritt, dann ist er schon zu).
                    // Schließen und Absenden laufen unverändert.
                    const fehler = Object.keys(await validateForm());
                    if (fehler.length > 0) {
                      formUsage.submitted('fail', fehler);
                    } else {
                      formUsage.submitted('ok');
                    }
                    dialogUsage.confirmed();
                    handleSubmit();
                    handleClose();
                  }}
                  variant="outlined"
                  disabled={!isValid || !dirty}
                >
                  Setzen
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Formik>
    </Box>
  );
}

export default AgentFormDismiss;
